/**
 * "Share this resource" — content generation and platform URLs.
 *
 * Deliberately resource-agnostic: every function takes a `ShareResource`
 * rather than assuming the homepage guide, so a future guide page can call
 * `openShare({ title, description, url }, 'guide:<slug>')` and get the same
 * platform-specific wording pointed at its own URL. See `HOMEPAGE_SHARE_RESOURCE`
 * below for the one resource wired up today.
 *
 * No backend, no share API, no SDK — every platform is reached through its own
 * public share URL (wa.me, intent/tweet, sharer.php, sharing/share-offsite,
 * t.me/share), which is the same mechanism a plain `<a href>` would use.
 */

import { absoluteUrl } from '@/config/site'

export interface ShareResource {
  title: string
  description: string
  /** Absolute URL. */
  url: string
}

export type SharePlatform = 'whatsapp' | 'linkedin' | 'x' | 'facebook' | 'telegram' | 'native'

export const SHARE_PLATFORMS: { id: SharePlatform; label: string }[] = [
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'x', label: 'X' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'telegram', label: 'Telegram' },
]

/*
 * The guide selected to introduce a first-time visitor to the product: broad
 * job-seeker relevance (not role-specific), already one of the four guides
 * promoted in the footer, and it naturally leads into the ATS Checker — read
 * the format rules, then go find out whether your own resume follows them.
 *
 * Hand-copied from that guide's own record (@/data/seo/pages's
 * getLandingPage), NOT read from it live: pages.ts computes its whole
 * LANDING_PAGES table from the full GUIDES content array
 * (@/data/seo/guides — the per-guide body copy), and this constant is needed by ShareModal via the persistent,
 * always-eager Layout header — importing getLandingPage here pulled that
 * entire chunk into every page's initial JS, homepage included, for three
 * short strings. share.test.ts asserts these three values still match
 * getLandingPage('/guides/ats-resume-format')'s real output, so a future
 * edit to that guide can't silently drift from what gets shared.
 */
export const HOMEPAGE_SHARE_RESOURCE: ShareResource = {
  title: 'ATS Resume Format Guide',
  description:
    'Which layouts, columns, fonts and spacing choices can break resume text extraction, which choices are safe, and how to test your own file.',
  url: absoluteUrl('/guides/ats-resume-format'),
}

const trim = (s: string): string => s.trim().replace(/\n{3,}/g, '\n\n')

const DEFAULT_BODY: Record<SharePlatform, (r: ShareResource) => string> = {
  whatsapp: () => 'I found this useful and thought it might help with your job search.',
  linkedin: (r) =>
    `💼 Looking to improve your resume for ATS screening?\n\n${r.description}\n\nWorth checking if you're currently applying for jobs or preparing your next resume.`,
  x: (r) => `🚀 ${r.title}\n\n${r.description}`,
  facebook: (r) => `📄 Working on your resume?\n\n${r.description}`,
  telegram: () => '📄 Found this useful for improving an ATS-friendly resume.',
  native: () => 'I found this useful and thought it might help with your job search.',
}

/**
 * The message body for a platform — personal message first (untouched), then
 * the platform's default line, but never the URL. Callers decide separately
 * whether the URL is appended to this text or passed as its own parameter,
 * since that differs per platform (WhatsApp has one field; X, Facebook,
 * Telegram and LinkedIn all accept the link separately).
 */
export const shareBody = (platform: SharePlatform, resource: ShareResource, personalMessage: string): string => {
  const lines = [personalMessage.trim(), DEFAULT_BODY[platform](resource)].filter(Boolean)
  let body = trim(lines.join('\n\n'))
  // X's own client truncates long posts anyway, but trimming here keeps the
  // preview honest about what will actually be posted (23 chars reserved for
  // the t.co-shortened link X appends after this text).
  if (platform === 'x' && body.length > 255) body = `${body.slice(0, 254).trimEnd()}…`
  return body
}

/** What the preview panel shows: the body, then the link on its own line — the same shape every platform actually renders. */
export const previewText = (platform: SharePlatform, resource: ShareResource, personalMessage: string): string =>
  `${shareBody(platform, resource, personalMessage)}\n\n${resource.url}`

/** The URL to open (in a new tab) for a given platform's share action. */
export const shareHref = (platform: SharePlatform, resource: ShareResource, personalMessage: string): string => {
  const url = encodeURIComponent(resource.url)
  const body = shareBody(platform, resource, personalMessage)
  const text = encodeURIComponent(body)
  switch (platform) {
    case 'whatsapp':
      // WhatsApp has one field, so the link has to live inside the text to appear at all.
      return `https://wa.me/?text=${encodeURIComponent(`${body}\n\n${resource.url}`)}`
    case 'linkedin':
      // The old `summary`/`title` params were retired in 2020 — this is the only
      // parameter LinkedIn's share dialog still honours, so the caption is
      // copied to the clipboard separately (see ShareModal) rather than passed here.
      return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`
    case 'x':
      return `https://twitter.com/intent/tweet?text=${text}&url=${url}`
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`
    case 'telegram':
      return `https://t.me/share/url?url=${url}&text=${text}`
    case 'native':
      return resource.url
  }
}
